import React, { type Ref } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { SearchSuggestionList } from '../SearchSuggestionList';
import { UserSearchList } from '../UserSearch/UserSearchList';
import { VisualSearchPromoBanner } from '../VisualSearchPromoBanner';

import styles from './styles.module.css';

import { type UserSearchResponse } from '@/modules/search/aliases';
import {
  type SearchSuggestionOption,
  SearchSuggestionType,
} from '@/modules/search/types';

interface SearchSuggestionsWithRecentProps {
  recentSuggestionsRef: Ref<HTMLUListElement>;
  popularSuggestionsRef?: Ref<HTMLUListElement>;
  clearSuggestionsRef: Ref<HTMLButtonElement>;
  searchSuggestions: SearchSuggestionOption[];
  onClick: (
    suggestion: SearchSuggestionOption,
    suggestionType: SearchSuggestionType
  ) => void;
  hasValue: boolean;
  recentSearchItems: SearchSuggestionOption[];
  clearRecentSearchHistory: () => void;
  popularSearchSuggestions?: SearchSuggestionOption[];
  isRefined: boolean;
  searchSuggestionsRef: Ref<HTMLUListElement>;
  isFullViewPort?: boolean;
  trendingSearchRef: Ref<HTMLUListElement>;
  userSuggestionsRef?: Ref<HTMLUListElement>;
  trendingSearchSuggestions: SearchSuggestionOption[];
  userSuggestions?: UserSearchResponse;
  isVariant?: boolean;
  showVisualSearchBanner?: boolean;
}

export function SearchSuggestions({
  searchSuggestions,
  hasValue,
  onClick,
  recentSearchItems,
  clearRecentSearchHistory,
  popularSearchSuggestions,
  recentSuggestionsRef,
  popularSuggestionsRef,
  clearSuggestionsRef,
  isRefined,
  searchSuggestionsRef,
  isFullViewPort,
  trendingSearchRef,
  userSuggestionsRef,
  trendingSearchSuggestions,
  userSuggestions,
  isVariant,
  showVisualSearchBanner,
}: SearchSuggestionsWithRecentProps) {
  const t = useTranslations('common');

  const hasSearchSuggestions = Boolean(searchSuggestions.length);
  const hasRecentSearches = Boolean(recentSearchItems.length);
  const hasPopularSuggestions = Boolean(popularSearchSuggestions?.length);
  const hasTrendingSearchSuggestions = Boolean(
    trendingSearchSuggestions.length
  );

  function hasVerticalPadding() {
    if (!isRefined) {
      return true;
    }
    if (hasValue) {
      return hasSearchSuggestions;
    }
    return false;
  }

  return (
    <div
      id="search-suggestions"
      role="listbox"
      className={clsx(styles.searchSuggestionsContainer, {
        [styles['searchSuggestionsContainer--fullViewPort']]: isFullViewPort,
        [styles['searchSuggestionsContainer--verticalPadding']]:
          hasVerticalPadding(),
        [styles['searchSuggestionsContainer--variant']]: !!isVariant,
      })}
      aria-label="Search suggestions"
    >
      {!hasSearchSuggestions && hasRecentSearches && !isRefined && (
        <>
          <div className={clsx(styles.suggestionListHeader)}>
            <Body
              variant="sm-regular"
              as="span"
              className={clsx(styles.suggestionTitle)}
            >
              {t('SearchSuggestions.Recent')}
            </Body>
            <button
              className={clsx(styles.suggestionClearButton)}
              onClick={clearRecentSearchHistory}
              // eslint-disable-next-line no-restricted-syntax
              data-testid="recentSearch__clear"
              value="clear"
              ref={clearSuggestionsRef}
            >
              <Body variant="sm-regular" as="span">
                {t('Clear')}
              </Body>
            </button>
          </div>
          <SearchSuggestionList
            suggestionsRef={recentSuggestionsRef}
            suggestions={recentSearchItems}
            type="recent"
            handleClick={onClick}
            suggestionType={SearchSuggestionType.RECENT}
          />
        </>
      )}
      {!hasSearchSuggestions &&
        hasPopularSuggestions &&
        popularSearchSuggestions &&
        !isRefined && (
          <>
            <div className={clsx(styles.suggestionListHeader)}>
              <Body
                variant="sm-regular"
                as="span"
                className={clsx(styles.suggestionTitle)}
              >
                {t('SearchSuggestions.Popular')}
              </Body>
            </div>
            <SearchSuggestionList
              suggestionsRef={popularSuggestionsRef}
              suggestions={popularSearchSuggestions}
              type="popular"
              handleClick={onClick}
              suggestionType={SearchSuggestionType.POPULAR}
            />
          </>
        )}
      {hasValue && isRefined && hasSearchSuggestions && (
        <>
          <SearchSuggestionList
            suggestionsRef={searchSuggestionsRef}
            suggestions={searchSuggestions}
            type="suggested"
            handleClick={onClick}
            suggestionType={SearchSuggestionType.POPULAR}
          />
        </>
      )}
      {!hasSearchSuggestions && hasTrendingSearchSuggestions && !isRefined && (
        <>
          <div className={clsx(styles.suggestionListHeader)}>
            <Body
              variant="sm-regular"
              as="span"
              className={clsx(styles.suggestionTitle)}
            >
              {t('SearchSuggestions.Trending')}
            </Body>
          </div>
          <SearchSuggestionList
            suggestionsRef={trendingSearchRef}
            suggestions={trendingSearchSuggestions}
            type="trending"
            handleClick={onClick}
            suggestionType={SearchSuggestionType.TRENDING}
          />
        </>
      )}
      {hasValue && isRefined && userSuggestions && (
        <UserSearchList
          userSuggestions={userSuggestions}
          userSuggestionsRef={userSuggestionsRef}
        />
      )}
      {!hasSearchSuggestions && showVisualSearchBanner && (
        <VisualSearchPromoBanner />
      )}
    </div>
  );
}
